import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { EventType, PublicClientApplication, type AuthenticationResult } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { App } from './App';
import { LocalApp } from './LocalApp';
import { PublicApp } from './public/PublicApp';
import { isPublicPath } from './public/publicRoutes';
import { StartupError } from './components/StartupError';
import { isAuthConfigured, msalConfig } from './auth/authConfig';
import './styles.css';

const root = createRoot(document.getElementById('root')!);

function render(children: ReactNode): void {
  root.render(<StrictMode>{children}</StrictMode>);
}

// Everything below runs before the first render, and anything that throws here would otherwise
// leave a blank page with the reason only in the browser console. So every path renders something.

// The public branch comes before everything else, including the Entra configuration checks. A job
// candidate filling in the CT assessment has no account in this tenant, so sign-in is not something
// they are missing — it is something this path must never reach for. Constructing MSAL here would
// also mean a stale redirect state or an unreachable authority could take down a page that has no
// use for either. See `public/publicRoutes.ts` for why one prefix rather than a list of paths.
if (isPublicPath(window.location.pathname)) {
  render(<PublicApp />);
}
// DEV comes next so the condition is statically decidable: Vite replaces `import.meta.env.DEV` with
// `false` in a production build, making this `if (false && …)` and letting dead-code elimination drop
// the branch and everything it imports. Written the other way round the minifier has to reason about
// a function call first, and the guarantee becomes a hope.
else if (import.meta.env.DEV && !isAuthConfigured()) {
  // No Entra values in a development build: run without credentials so the whole app can be used
  // before any tenant work is done. The API has a matching mode.
  console.warn(
    'Clinic Resource Hub: running in local development mode. No sign-in, and drafts are written to ' +
      'disk as .eml files instead of being created in a mailbox.',
  );

  render(<LocalApp />);
} else if (!isAuthConfigured()) {
  render(
    <StartupError title="Sign-in is not configured">
      <p>
        This build has no Entra ID values. Set <code>VITE_SPA_CLIENT_ID</code>,{' '}
        <code>VITE_TENANT_ID</code> and <code>VITE_API_SCOPE</code>, then reload.
      </p>
      <p>
        Locally that means copying <code>.env.example</code> to <code>.env.local</code> and filling
        it in — see <code>docs/entra-setup.md</code> for where each value comes from.
      </p>
    </StartupError>,
  );
} else {
  try {
    const msalInstance = new PublicClientApplication(msalConfig);

    // MSAL v5 must be initialised before any other call, and handleRedirectPromise has to run
    // before the first render so a redirect back from Entra is consumed rather than treated as a
    // cold start.
    await msalInstance.initialize();
    await msalInstance.handleRedirectPromise();

    if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
      msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
    }

    msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        msalInstance.setActiveAccount((event.payload as AuthenticationResult).account);
      }
    });

    render(
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>,
    );
  } catch (error) {
    // Most often a stale or half-written redirect state in sessionStorage — an interrupted sign-in
    // leaves one behind, and MSAL then refuses to start. Clearing it is the fix, so offer that
    // rather than making someone find it in the console.
    render(
      <StartupError title="Could not start the sign-in session" offerSessionReset>
        <p>{error instanceof Error ? error.message : 'An unknown error occurred while starting up.'}</p>
        <p>
          If clearing the session does not help, check that the Entra ID values in this build are
          correct and that this origin is registered as a redirect URI.
        </p>
      </StartupError>,
    );
  }
}
